import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './DocumentList.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Session storage cache keys
const CACHE_KEY = 'documentListCache';
const FILTERS_KEY = 'documentListFilters';

// Get cached data from session storage
const getCachedData = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      // Cache expires after 5 minutes
      if (Date.now() - data.timestamp < 5 * 60 * 1000) {
        return data;
      }
    }
  } catch (e) {
    console.error('Error reading cache:', e);
  }
  return null;
};

// Save data to session storage cache
const setCachedData = (cacheKey, documents, totalCount, totalPages) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      cacheKey,
      documents,
      totalCount,
      totalPages,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error('Error saving cache:', e);
  }
};

// Get saved filters from session storage
const getSavedFilters = () => {
  try {
    const saved = sessionStorage.getItem(FILTERS_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error reading saved filters:', e);
  }
  return null;
};

// Save filters to session storage
const saveFilters = (filters) => {
  try {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch (e) {
    console.error('Error saving filters:', e);
  }
};

const DocumentList = ({ refreshTrigger, onGenerateMetadataClick, onFilteredDocumentsChange }) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  
  // Read initial values from sessionStorage (more reliable than URL params with React Router)
  const getInitialFilters = () => {
    // First try sessionStorage (most reliable)
    const saved = getSavedFilters();
    if (saved) {
      return {
        fileNameInput: saved.fileNameInput || saved.fileNameFilter || '',
        fileNameFilter: saved.fileNameFilter || '',
        fileTypeFilter: saved.fileTypeFilter || '',
        generatedMetadataFilter: saved.generatedMetadataFilter || '',
        insurersFilter: saved.insurersFilter || (saved.insurerFilter ? [saved.insurerFilter] : []),
        issueTypeFilter: saved.issueTypeFilter || '',
        cptCodesFilter: saved.cptCodesFilter || [],
        currentPage: saved.currentPage || 1,
        itemsPerPage: saved.itemsPerPage || 25,
      };
    }
    // Fallback to URL params
    const params = new URLSearchParams(window.location.search);
    const fileNameFromUrl = params.get('fileName') || '';
    const cptCodesFromUrl = params.get('cptCodes') ? params.get('cptCodes').split(',') : [];
    const insurersFromUrl = params.get('insurers') ? params.get('insurers').split(',') : [];
    return {
      fileNameInput: fileNameFromUrl,
      fileNameFilter: fileNameFromUrl,
      fileTypeFilter: params.get('fileType') || '',
      generatedMetadataFilter: params.get('metadata') || '',
      insurersFilter: insurersFromUrl,
      issueTypeFilter: params.get('issueType') || '',
      cptCodesFilter: cptCodesFromUrl,
      currentPage: parseInt(params.get('page')) || 1,
      itemsPerPage: parseInt(params.get('pageSize')) || 25,
    };
  };
  
  const handleRowClick = useCallback((e, docId) => {
    const url = `/document_view/${docId}`;
    // Check for Ctrl (Windows/Linux) or Cmd (Mac) key for new tab
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  }, [navigate]);
  
  // Try to load cached data on initial render
  const cachedData = useMemo(() => getCachedData(), []);
  
  const [documents, setDocuments] = useState(cachedData?.documents || []);
  const [loading, setLoading] = useState(!cachedData?.documents);
  const [isFiltering, setIsFiltering] = useState(false); // Subtle loading for filter changes
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Filter states - initialize from sessionStorage using lazy initialization
  const [fileNameInput, setFileNameInput] = useState(() => getInitialFilters().fileNameInput);
  const [fileNameFilter, setFileNameFilter] = useState(() => getInitialFilters().fileNameFilter);
  const [fileTypeFilter, setFileTypeFilter] = useState(() => getInitialFilters().fileTypeFilter);
  const [generatedMetadataFilter, setGeneratedMetadataFilter] = useState(() => getInitialFilters().generatedMetadataFilter);
  const [insurersFilter, setInsurersFilter] = useState(() => getInitialFilters().insurersFilter || []);
  const [issueTypeFilter, setIssueTypeFilter] = useState(() => getInitialFilters().issueTypeFilter);
  const [cptCodesFilter, setCptCodesFilter] = useState(() => getInitialFilters().cptCodesFilter);
  
  // File name search state (dropdown)
  const [fileNameSearchResults, setFileNameSearchResults] = useState([]);
  const [showFileNameDropdown, setShowFileNameDropdown] = useState(false);
  const [loadingFileNameSearch, setLoadingFileNameSearch] = useState(false);
  const fileNameDropdownRef = useRef(null);
  
  // CPT code search state
  const [cptSearchQuery, setCptSearchQuery] = useState('');
  const [cptSearchResults, setCptSearchResults] = useState([]);
  const [showCptDropdown, setShowCptDropdown] = useState(false);
  const [loadingCptSearch, setLoadingCptSearch] = useState(false);
  const cptDropdownRef = useRef(null);
  
  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Server-side pagination states - initialize from URL or cache
  const [currentPage, setCurrentPage] = useState(() => getInitialFilters().currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(() => getInitialFilters().itemsPerPage);
  const [totalCount, setTotalCount] = useState(cachedData?.totalCount || 0);
  const [totalPages, setTotalPages] = useState(cachedData?.totalPages || 1);
  
  // Track if this is the first render to avoid resetting page on mount
  const isFirstRender = useRef(true);
  
  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    saveFilters({
      fileNameInput,
      fileNameFilter,
      fileTypeFilter,
      generatedMetadataFilter,
      insurersFilter,
      issueTypeFilter,
      cptCodesFilter,
      currentPage,
      itemsPerPage,
    });
  }, [fileNameInput, fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter, cptCodesFilter, currentPage, itemsPerPage]);
  
  // Update URL when filters change (but not on first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    const params = new URLSearchParams();
    if (fileNameFilter) params.set('fileName', fileNameFilter);
    if (fileTypeFilter) params.set('fileType', fileTypeFilter);
    if (generatedMetadataFilter) params.set('metadata', generatedMetadataFilter);
    if (insurersFilter.length > 0) params.set('insurers', insurersFilter.join(','));
    if (issueTypeFilter) params.set('issueType', issueTypeFilter);
    if (cptCodesFilter.length > 0) params.set('cptCodes', cptCodesFilter.join(','));
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (itemsPerPage !== 25) params.set('pageSize', itemsPerPage.toString());
    
    // Update URL without causing navigation
    setSearchParams(params, { replace: true });
  }, [fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter, cptCodesFilter, currentPage, itemsPerPage, setSearchParams]);

  // File name search functionality
  useEffect(() => {
    const searchFileNames = async () => {
      if (fileNameInput.length < 2) {
        setFileNameSearchResults([]);
        return;
      }
      
      setLoadingFileNameSearch(true);
      try {
        // Search for matching file names from the API
        const response = await axios.get(`${API_BASE_URL}/insurance-documents?page=1&page_size=20&file_name=${encodeURIComponent(fileNameInput)}`);
        const uniqueNames = [...new Set(response.data.items.map(doc => doc.file_name))];
        setFileNameSearchResults(uniqueNames.slice(0, 15));
    } catch (err) {
        console.error('Error searching file names:', err);
        setFileNameSearchResults([]);
      } finally {
        setLoadingFileNameSearch(false);
      }
    };

    const debounce = setTimeout(searchFileNames, 300);
    return () => clearTimeout(debounce);
  }, [fileNameInput]);

  // Close file name dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fileNameDropdownRef.current && !fileNameDropdownRef.current.contains(event.target)) {
        setShowFileNameDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Select a file name from dropdown
  const handleFileNameSelect = (fileName) => {
    setFileNameInput(''); // Clear input so user can search for another
    setFileNameFilter(fileName);
    setShowFileNameDropdown(false);
    setCurrentPage(1);
  };

  // Clear file name filter
  const handleClearFileName = () => {
    setFileNameInput('');
    setFileNameFilter('');
    setShowFileNameDropdown(false);
  };

  // Handle Enter key in file name input
  const handleFileNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      setFileNameFilter(fileNameInput);
      setShowFileNameDropdown(false);
      setCurrentPage(1);
    }
  };

  // CPT code search functionality
  useEffect(() => {
    const searchCptCodes = async () => {
      if (cptSearchQuery.length < 2) {
        setCptSearchResults([]);
        return;
      }
      
      setLoadingCptSearch(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/cpt-codes/search/codes?query=${encodeURIComponent(cptSearchQuery)}&limit=30`);
        setCptSearchResults(response.data);
    } catch (err) {
        console.error('Error searching CPT codes:', err);
        setCptSearchResults([]);
    } finally {
        setLoadingCptSearch(false);
      }
    };

    const debounce = setTimeout(searchCptCodes, 300);
    return () => clearTimeout(debounce);
  }, [cptSearchQuery]);

  // Close CPT dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cptDropdownRef.current && !cptDropdownRef.current.contains(event.target)) {
        setShowCptDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle CPT code selection
  const handleCptCodeToggle = (code) => {
    setCptCodesFilter(prev => {
      if (prev.includes(code)) {
        return prev.filter(c => c !== code);
      } else {
        return [...prev, code];
      }
    });
    setCptSearchQuery('');
    setShowCptDropdown(false);
  };

  // Remove a selected CPT code
  const handleRemoveCptCode = (code) => {
    setCptCodesFilter(prev => prev.filter(c => c !== code));
  };

  // Fetch documents function wrapped in useCallback for stable reference
  const fetchDocuments = useCallback(async () => {
    // Build query parameters for server-side filtering
    const params = new URLSearchParams();
    params.append('page', currentPage.toString());
    params.append('page_size', itemsPerPage.toString());
    
    // Add filters only if they have values
    if (fileNameFilter) {
      params.append('file_name', fileNameFilter);
    }
    if (fileTypeFilter) {
      params.append('file_type', fileTypeFilter);
    }
    // Handle multiple insurers filter
    if (insurersFilter.length > 0) {
      if (insurersFilter.includes('__no_insurer__')) {
        // If "No Insurer" is selected along with others, we need special handling
        if (insurersFilter.length === 1) {
          params.append('has_guideline', 'false');
        } else {
          // Multiple insurers including "No Insurer" - filter by actual insurers
          const realInsurers = insurersFilter.filter(i => i !== '__no_insurer__');
          params.append('insurers', realInsurers.join(','));
        }
      } else {
        params.append('insurers', insurersFilter.join(','));
      }
    }
    if (generatedMetadataFilter === 'yes') {
      params.append('has_guideline', 'true');
    } else if (generatedMetadataFilter === 'no') {
      params.append('has_guideline', 'false');
    }
    if (issueTypeFilter) {
      params.append('issue_type', issueTypeFilter);
    }
    if (cptCodesFilter.length > 0) {
      params.append('cpt_codes', cptCodesFilter.join(','));
    }
    
    const cacheKey = params.toString();
    
    // Check if we have valid cached data for this exact query
    const cached = getCachedData();
    if (cached && cached.cacheKey === cacheKey) {
      // Use cached data immediately - no loading spinner needed
      setDocuments(cached.documents);
      setTotalCount(cached.totalCount);
      setTotalPages(cached.totalPages);
      setLoading(false);
      setIsFiltering(false);
      return; // Skip the API call if cache is fresh
    }
    
    try {
      // Only show full loading spinner on initial load (no documents yet)
      if (documents.length === 0) {
      setLoading(true);
      } else {
        setIsFiltering(true);
      }
      
      const response = await axios.get(`${API_BASE_URL}/insurance-documents?${params.toString()}`);
      
      // Handle new paginated response format
      const { items, pagination } = response.data;
      setDocuments(items);
      setTotalCount(pagination.total_count);
      setTotalPages(pagination.total_pages);
      setError(null);
      
      // Cache the results
      setCachedData(cacheKey, items, pagination.total_count, pagination.total_pages);
    } catch (err) {
      setError(`Failed to load documents: ${err.response?.data?.detail || err.message}`);
      setDocuments([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  }, [currentPage, itemsPerPage, fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter, cptCodesFilter, documents.length]);

  // Fetch dashboard stats (with filters for dynamic metrics)
  // showLoading = only show loading spinner on initial load, not on filter changes
  const fetchDashboardStats = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoadingStats(true);
      }
      
      // Always build URL with current filters
      const params = new URLSearchParams();
      if (fileNameFilter) params.append('file_name', fileNameFilter);
      if (fileTypeFilter) params.append('file_type', fileTypeFilter);
      if (insurersFilter.length > 0) {
        const realInsurers = insurersFilter.filter(i => i !== '__no_insurer__');
        if (realInsurers.length > 0) {
          params.append('insurers', realInsurers.join(','));
        }
        if (insurersFilter.includes('__no_insurer__') && insurersFilter.length === 1) {
          params.append('has_guideline', 'false');
        }
      }
      if (issueTypeFilter) params.append('issue_type', issueTypeFilter);
      if (cptCodesFilter.length > 0) params.append('cpt_codes', cptCodesFilter.join(','));
      
      let url = `${API_BASE_URL}/insurance-documents/dashboard-stats`;
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setDashboardStats(response.data);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [fileNameFilter, fileTypeFilter, insurersFilter, issueTypeFilter, cptCodesFilter]);

  // Fetch dashboard stats on initial mount (with loading)
  const dashboardInitialized = useRef(false);
  useEffect(() => {
    if (!dashboardInitialized.current) {
      dashboardInitialized.current = true;
      fetchDashboardStats(true); // Show loading on initial fetch
    }
  }, [fetchDashboardStats]);

  // Fetch dashboard stats when filters change (without loading flash)
  useEffect(() => {
    if (dashboardInitialized.current) {
      fetchDashboardStats(false); // No loading on filter changes
    }
  }, [fetchDashboardStats, fileNameFilter, fileTypeFilter, insurersFilter, issueTypeFilter, cptCodesFilter]);
  
  // Refresh dashboard stats when documents change (e.g., after upload)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchDashboardStats(false);
    }
  }, [refreshTrigger, fetchDashboardStats]);

  // Track previous refresh trigger to detect changes
  const prevRefreshTrigger = useRef(refreshTrigger);
  
  // Fetch documents when pagination or filters change
  useEffect(() => {
    // If refreshTrigger changed, invalidate cache first
    if (prevRefreshTrigger.current !== refreshTrigger) {
      prevRefreshTrigger.current = refreshTrigger;
      sessionStorage.removeItem(CACHE_KEY);
    }
    fetchDocuments();
  }, [fetchDocuments, refreshTrigger]);

  // Track previous filter values to detect actual changes (not just initial mount)
  const prevFiltersRef = useRef({ fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter, cptCodesFilter, itemsPerPage });
  
  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged = 
      prev.fileNameFilter !== fileNameFilter ||
      prev.fileTypeFilter !== fileTypeFilter ||
      prev.generatedMetadataFilter !== generatedMetadataFilter ||
      JSON.stringify(prev.insurersFilter) !== JSON.stringify(insurersFilter) ||
      prev.issueTypeFilter !== issueTypeFilter ||
      JSON.stringify(prev.cptCodesFilter) !== JSON.stringify(cptCodesFilter) ||
      prev.itemsPerPage !== itemsPerPage;
    
    if (filtersChanged) {
      prevFiltersRef.current = { fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter, cptCodesFilter, itemsPerPage };
      // Only reset page if filters actually changed after initial mount
      if (!isFirstRender.current) {
    setCurrentPage(1);
      }
    }
  }, [fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter, cptCodesFilter, itemsPerPage]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Dashboard click handlers
  // First row (stats): single-select - only one metric active at a time
  // Other rows: multi-select / toggle
  const handleDashboardClick = (filterType, value) => {
    setCurrentPage(1);
    
    // Check if this is a first-row stat click (single-select behavior)
    const isFirstRowStat = (
      (['total', 'generated', 'issue'].includes(filterType) && 
       ['total', 'yes', 'no', 'all_issues', 'no_issues'].includes(value)) || 
      filterType === 'total'
    );
    
    if (isFirstRowStat) {
      // Single-select: clear other first-row filters before applying
      if (filterType === 'total') {
        // Clear all filters
        setFileNameInput('');
        setFileNameFilter('');
        setFileTypeFilter('');
        setGeneratedMetadataFilter('');
        setInsurersFilter([]);
        setIssueTypeFilter('');
        setCptCodesFilter([]);
      } else if (filterType === 'generated') {
        // Clear issue filter, toggle generated
        setIssueTypeFilter('');
        setGeneratedMetadataFilter(prev => prev === value ? '' : value);
      } else if (filterType === 'issue' && (value === 'all_issues' || value === 'no_issues')) {
        // Clear generated filter, toggle issue
        setGeneratedMetadataFilter('');
        setIssueTypeFilter(prev => prev === value ? '' : value);
      }
    } else {
      // Non-first-row: keep existing behavior
      switch (filterType) {
        case 'issue':
          // Issue type tags (missing_cpt, unmatched_refs) - toggle
          setIssueTypeFilter(prev => prev === value ? '' : value);
          break;
        case 'insurer':
          // Toggle insurer in multi-select array
          setInsurersFilter(prev => {
            if (prev.includes(value)) {
              return prev.filter(i => i !== value);
            } else {
              return [...prev, value];
            }
          });
          break;
        case 'fileType':
          // Toggle file type filter
          setFileTypeFilter(prev => prev === value ? '' : value);
          break;
        default:
          break;
      }
    }
  };

  // Check if a dashboard filter is active
  const isFilterActive = (filterType, value) => {
    switch (filterType) {
      case 'generated':
        return generatedMetadataFilter === value;
      case 'issue':
        return issueTypeFilter === value;
      case 'insurer':
        return insurersFilter.includes(value);
      case 'fileType':
        return fileTypeFilter === value;
      default:
        return false;
      }
  };

  // Check if any filter is active
  const hasActiveFilters = fileNameFilter || fileTypeFilter || generatedMetadataFilter || insurersFilter.length > 0 || issueTypeFilter || cptCodesFilter.length > 0;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Track previous filter state to avoid unnecessary parent updates
  const prevFilterStateRef = useRef(null);

  // Function to fetch ALL document IDs matching current filters (for batch processing)
  const fetchAllFilteredIds = useCallback(async () => {
    try {
      // Build query parameters - same as fetchDocuments but with large page_size
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('page_size', '10000'); // Fetch all matching documents
      
      if (fileNameFilter) {
        params.append('file_name', fileNameFilter);
      }
      if (fileTypeFilter) {
        params.append('file_type', fileTypeFilter);
      }
      // Handle multiple insurers filter
      if (insurersFilter.length > 0) {
        if (insurersFilter.includes('__no_insurer__')) {
          if (insurersFilter.length === 1) {
            params.append('has_guideline', 'false');
          } else {
            const realInsurers = insurersFilter.filter(i => i !== '__no_insurer__');
            params.append('insurers', realInsurers.join(','));
          }
        } else {
          params.append('insurers', insurersFilter.join(','));
        }
      }
      if (generatedMetadataFilter === 'yes') {
        params.append('has_guideline', 'true');
      } else if (generatedMetadataFilter === 'no') {
        params.append('has_guideline', 'false');
      }
      if (issueTypeFilter) {
        params.append('issue_type', issueTypeFilter);
      }
      
      const response = await axios.get(`${API_BASE_URL}/insurance-documents?${params.toString()}`);
      return response.data.items.map(doc => doc.id);
    } catch (err) {
      console.error('Error fetching all filtered IDs:', err);
      return [];
    }
  }, [fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter]);

  // Notify parent component of filter state and provide function to get all IDs
  useEffect(() => {
    if (onFilteredDocumentsChange) {
      // Create a filter state object to track changes
      const filterState = {
        fileNameFilter: fileNameFilter,
        fileTypeFilter,
        generatedMetadataFilter,
        insurersFilter,
        issueTypeFilter,
        totalCount
      };
      const filterStateString = JSON.stringify(filterState);
      
      // Only call callback if the filter state actually changed
      if (prevFilterStateRef.current !== filterStateString) {
        prevFilterStateRef.current = filterStateString;
        
        // Check if any filters are active
        const hasActiveFilters = fileNameFilter || fileTypeFilter || 
          generatedMetadataFilter || insurersFilter.length > 0 || issueTypeFilter || cptCodesFilter.length > 0;
        
        // Pass filter info and the function to fetch all IDs
        onFilteredDocumentsChange({
          hasActiveFilters,
          totalCount,
          fetchAllIds: fetchAllFilteredIds
        });
      }
    }
  }, [fileNameFilter, fileTypeFilter, generatedMetadataFilter, insurersFilter, issueTypeFilter, cptCodesFilter, totalCount, onFilteredDocumentsChange, fetchAllFilteredIds]);

  // Calculate display range
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + documents.length, totalCount);

  const handleDeleteClick = (docId) => {
    setDeletingDocId(docId);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deletingDocId) {
      return;
    }

    try {
      setDeleting(true);
      const response = await axios.delete(`${API_BASE_URL}/insurance-documents/${deletingDocId}`);
      
      console.log('Document deleted:', response.data);
      
      // Invalidate cache and refresh the document list
      sessionStorage.removeItem(CACHE_KEY);
      await fetchDocuments();
      
      // Close the confirmation dialog
      setShowDeleteConfirm(false);
      setDeletingDocId(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      alert(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  // Only show full loading spinner on initial load (no documents loaded yet)
  if (loading && documents.length === 0) {
    return (
      <div className="document-list-loading">
        <div className="spinner"></div>
        <p>Loading documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-list-error">
        <span className="error-icon">❌</span>
        <span>{error}</span>
        <button onClick={fetchDocuments} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="document-list">
      <div className="document-list-header">
        <h2>Insurance Documents</h2>
        <div className="header-actions">
          {hasActiveFilters && (
            <button 
              className="clear-all-filters-btn"
              onClick={() => handleDashboardClick('total', null)}
            >
              Clear All Filters
            </button>
          )}
        {onGenerateMetadataClick && (
          <button
            onClick={onGenerateMetadataClick}
            className="generate-metadata-button"
          >
              Generate Metadata ({totalCount.toLocaleString()})
          </button>
        )}
        </div>
      </div>

      {/* Interactive Dashboard - Click any metric to filter */}
      {dashboardStats && !loadingStats && (
        <div className="documents-dashboard">
          {/* Main Stats Row */}
          <div className="stats-grid">
            <button 
              className={`stat-card total ${!hasActiveFilters ? 'active' : ''}`}
              onClick={() => handleDashboardClick('total', null)}
            >
              <div className="stat-indicator"></div>
              <div className="stat-content">
                <span className="stat-value">{dashboardStats.total_documents.toLocaleString()}</span>
                <span className="stat-label">Total Documents</span>
        </div>
            </button>
            
            <button 
              className={`stat-card generated ${isFilterActive('generated', 'yes') ? 'active' : ''}`}
              onClick={() => handleDashboardClick('generated', 'yes')}
            >
              <div className="stat-indicator"></div>
              <div className="stat-content">
                <span className="stat-value">{dashboardStats.generated.count.toLocaleString()}</span>
                <span className="stat-label">Generated</span>
                <span className="stat-percent">{dashboardStats.generated.percentage}%</span>
        </div>
            </button>
            
            <button 
              className={`stat-card pending ${isFilterActive('generated', 'no') ? 'active' : ''}`}
              onClick={() => handleDashboardClick('generated', 'no')}
            >
              <div className="stat-indicator"></div>
              <div className="stat-content">
                <span className="stat-value">{dashboardStats.not_generated.count.toLocaleString()}</span>
                <span className="stat-label">Pending</span>
                <span className="stat-percent">{dashboardStats.not_generated.percentage}%</span>
        </div>
            </button>
            
            <button 
              className={`stat-card issues ${isFilterActive('issue', 'all_issues') ? 'active' : ''}`}
              onClick={() => handleDashboardClick('issue', 'all_issues')}
            >
              <div className="stat-indicator"></div>
              <div className="stat-content">
                <span className="stat-value">{dashboardStats.issues.any_issues.toLocaleString()}</span>
                <span className="stat-label">With Issues</span>
        </div>
            </button>
            
            <button 
              className={`stat-card clean ${isFilterActive('issue', 'no_issues') ? 'active' : ''}`}
              onClick={() => handleDashboardClick('issue', 'no_issues')}
            >
              <div className="stat-indicator"></div>
              <div className="stat-content">
                <span className="stat-value">{dashboardStats.issues.no_issues.toLocaleString()}</span>
                <span className="stat-label">No Issues</span>
              </div>
            </button>
        </div>
        
          {/* Issue Type & File Type Row */}
          <div className="filters-row">
            <div className="breakdown-group">
              <h4 className="breakdown-title">Issue Type</h4>
              <div className="tag-list">
                <button 
                  className={`tag missing-cpt ${isFilterActive('issue', 'missing_cpt') ? 'active' : ''}`}
                  onClick={() => handleDashboardClick('issue', 'missing_cpt')}
                >
                  <span className="tag-name">Missing CPT</span>
                  <span className="tag-count">{dashboardStats.issues.missing_cpt}</span>
                </button>
                <button 
                  className={`tag unmatched ${isFilterActive('issue', 'unmatched_refs') ? 'active' : ''}`}
                  onClick={() => handleDashboardClick('issue', 'unmatched_refs')}
                >
                  <span className="tag-name">Unmatched Refs</span>
                  <span className="tag-count">{dashboardStats.issues.unmatched_refs}</span>
                </button>
              </div>
        </div>
        
            <div className="breakdown-group">
              <h4 className="breakdown-title">File Type</h4>
              <div className="tag-list">
                {dashboardStats.file_type_breakdown.map((item) => (
                  <button
                    key={item.file_type}
                    className={`tag file-type ${isFilterActive('fileType', item.file_type) ? 'active' : ''}`}
                    onClick={() => handleDashboardClick('fileType', item.file_type)}
                  >
                    <span className="tag-name">{item.file_type}</span>
                    <span className="tag-count">{item.count}</span>
                  </button>
                ))}
              </div>
        </div>
      </div>
      
          {/* Insurers Row - Separate for more space */}
          <div className="insurers-row">
            <h4 className="breakdown-title">Insurer</h4>
            <div className="insurers-list">
              {dashboardStats.insurer_breakdown.map((item) => (
          <button
                  key={item.insurer}
                  className={`tag insurer ${isFilterActive('insurer', item.insurer === 'No Insurer' ? '__no_insurer__' : item.insurer) ? 'active' : ''}`}
                  onClick={() => handleDashboardClick('insurer', item.insurer === 'No Insurer' ? '__no_insurer__' : item.insurer)}
                >
                  <span className="tag-name">{item.insurer}</span>
                  <span className="tag-count">{item.count}</span>
          </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {loadingStats && (
        <div className="dashboard-loading">
          <div className="spinner-small"></div>
          <span>Loading dashboard...</span>
        </div>
      )}
      
      {/* Search Filters - Outside Dashboard Box */}
      <div className="search-filters-row">
        {/* File Name Search with Dropdown */}
        <div className="file-search-container" ref={fileNameDropdownRef}>
          <label className="search-label">File Name</label>
          <div className="file-input-wrapper">
            <input
              type="text"
              className="file-search-input"
              placeholder="Search file names..."
              value={fileNameInput}
              onChange={(e) => {
                setFileNameInput(e.target.value);
                setShowFileNameDropdown(true);
              }}
              onFocus={() => setShowFileNameDropdown(true)}
              onKeyDown={handleFileNameKeyDown}
            />
            {fileNameFilter && (
              <button 
                className="file-clear-btn"
                onClick={handleClearFileName}
              >
                ×
              </button>
            )}
          </div>
          
          {/* Selected file name as tag */}
          {fileNameFilter && (
            <div className="file-selected-tag">
              <span className="file-tag">
                {fileNameFilter.length > 30 ? fileNameFilter.slice(0, 30) + '...' : fileNameFilter}
                <button 
                  className="file-tag-remove"
                  onClick={handleClearFileName}
                >
                  ×
                </button>
            </span>
            </div>
          )}
          
          {/* Dropdown with search results */}
          {showFileNameDropdown && fileNameInput.length >= 2 && (
            <div className="file-dropdown">
              {loadingFileNameSearch ? (
                <div className="file-dropdown-loading">Searching...</div>
              ) : fileNameSearchResults.length > 0 ? (
                fileNameSearchResults.map(fileName => (
                  <div
                    key={fileName}
                    className={`file-dropdown-item ${fileNameFilter === fileName ? 'selected' : ''}`}
                    onClick={() => handleFileNameSelect(fileName)}
                  >
                    <span className="file-name">{fileName.length > 50 ? fileName.slice(0, 50) + '...' : fileName}</span>
                    {fileNameFilter === fileName && <span className="file-check">✓</span>}
                  </div>
                ))
              ) : (
                <div className="file-dropdown-empty">No matching files found</div>
              )}
            </div>
          )}
        </div>
        
        {/* CPT Code Multi-Select */}
        <div className="file-search-container" ref={cptDropdownRef}>
          <label className="search-label">CPT Codes</label>
          <div className="file-input-wrapper">
            <input
              type="text"
              className="file-search-input"
              placeholder="Search CPT codes..."
              value={cptSearchQuery}
              onChange={(e) => {
                setCptSearchQuery(e.target.value);
                setShowCptDropdown(true);
              }}
              onFocus={() => setShowCptDropdown(true)}
            />
            {cptCodesFilter.length > 0 && (
              <button 
                className="file-clear-btn"
                onClick={() => setCptCodesFilter([])}
                title="Clear all CPT codes"
              >
                ×
          </button>
            )}
          </div>
          
          {/* Selected CPT codes as tags */}
          {cptCodesFilter.length > 0 && (
            <div className="file-selected-tag">
              {cptCodesFilter.map(code => (
                <span key={code} className="file-tag">
                  {code}
                  <button 
                    className="file-tag-remove"
                    onClick={() => handleRemoveCptCode(code)}
                  >
                    ×
                  </button>
                </span>
              ))}
        </div>
      )}
      
          {/* Dropdown with search results */}
          {showCptDropdown && (cptSearchQuery.length >= 2 || cptSearchResults.length > 0) && (
            <div className="file-dropdown">
              {loadingCptSearch ? (
                <div className="file-dropdown-loading">Searching...</div>
              ) : cptSearchResults.length > 0 ? (
                cptSearchResults.map(result => (
                  <div
                    key={result.cpt_code}
                    className={`file-dropdown-item ${cptCodesFilter.includes(result.cpt_code) ? 'selected' : ''}`}
                    onClick={() => handleCptCodeToggle(result.cpt_code)}
                  >
                    <span className="file-name">{result.cpt_code} - {result.description?.slice(0, 40)}{result.description?.length > 40 ? '...' : ''}</span>
                    {cptCodesFilter.includes(result.cpt_code) && <span className="file-check">✓</span>}
                  </div>
                ))
              ) : cptSearchQuery.length >= 2 ? (
                <div className="file-dropdown-empty">No CPT codes found</div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      
      {!loading && documents.length === 0 && totalCount === 0 ? (
        <div className="no-documents">
          <p>{fileNameFilter || fileTypeFilter || generatedMetadataFilter || insurersFilter.length > 0 || issueTypeFilter
            ? 'No documents match the current filters.' 
            : 'No documents found. Upload a PDF to get started.'}</p>
        </div>
      ) : (
        <>
          {/* Table info */}
          <div className="table-info">
            <span>
              Showing {totalCount > 0 ? startIndex + 1 : 0}-{endIndex} of {totalCount} documents
            </span>
            <div className="items-per-page">
              <label htmlFor="itemsPerPage">Per page:</label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className={`documents-table-container ${isFiltering ? 'filtering' : ''}`}>
            {isFiltering && <div className="filter-loading-overlay"><div className="filter-spinner"></div></div>}
            <div className="documents-table">
              <table>
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>File Type</th>
                    <th>Extracted Timestamp</th>
                    <th>Generated Metadata</th>
                    <th>Issues</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr 
                      key={doc.id}
                      className="clickable-row"
                      onClick={(e) => {
                        e.preventDefault();
                        handleRowClick(e, doc.id);
                      }}
                      onAuxClick={(e) => {
                        // Middle mouse button (button === 1) opens in new tab
                        if (e.button === 1) {
                          e.preventDefault();
                          handleRowClick(e, doc.id);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="file-name-cell">{doc.file_name}</td>
                      <td>{doc.file_type || 'N/A'}</td>
                      <td>{formatDate(doc.extracted_timestamp)}</td>
                      <td className="guideline-status-cell">
                        {doc.has_guideline ? (
                          <span className="guideline-badge has-guideline" title="Guideline metadata exists">
                            <span className="badge-icon">✓</span><span className="badge-text">Yes</span>
                          </span>
                        ) : (
                          <span className="guideline-badge no-guideline" title="No guideline metadata">
                            <span className="badge-icon">✗</span><span className="badge-text">No</span>
                          </span>
                        )}
                      </td>
                      <td className="issues-cell">
                        {doc.issues_count > 0 ? (
                          <div className="issues-badges">
                            {doc.has_unmatched_references && (
                              <span className="issue-badge issue-ref" title="Some referenced guideline IDs do not exist">
                                🔗 Bad Refs
                              </span>
                            )}
                            {doc.has_missing_cpt_codes && (
                              <span className="issue-badge issue-cpt" title="No CPT codes defined">
                                📋 No CPT
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="no-issues">—</span>
                        )}
                      </td>
                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteClick(doc.id);
                          }}
                          className="delete-button-list"
                          disabled={deleting}
                        >
                          {deleting && deletingDocId === doc.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                ‹
              </button>
              
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                )
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                »
              </button>
            </div>
          )}
        </>
      )}

      {showDeleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-dialog">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this document?</p>
            <p className="delete-warning">
              This will permanently delete the document and all associated guidelines.
            </p>
            <div className="delete-confirm-buttons">
              <button 
                onClick={handleDelete} 
                className="confirm-delete-button"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button 
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingDocId(null);
                }}
                className="cancel-delete-button"
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;

